import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { AuthSession } from './core/auth/auth-session';
import { authGuard, requireAccess } from './core/auth/auth.guards';
import { AUTH_POLICIES, homeForSession } from './core/auth/auth-policies';
import { billOfQuantitiesStepGuard } from './shared/guards/billOfQuantitiesStepGuard';
import { unsavedChangesGuard } from './shared/guards/unsavedChangesGuard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/login/login').then((m) => m.Login),
  },
  {
    path: 'change-password/:token',
    loadComponent: () =>
      import('./features/change-password/change-password').then((m) => m.ChangePassword),
  },
  {
    path: 'change-password',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/change-password/change-password').then((m) => m.ChangePassword),
  },
  {
    path: 'user-registration',
    loadComponent: () =>
      import('./features/user-registration/user-registration').then((m) => m.UserRegistration),
  },
  {
    path: 'access-denied',
    loadComponent: () =>
      import('./features/access-denied/access-denied').then((m) => m.AccessDenied),
  },
  {
    path: 'account',
    canActivate: [authGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./features/account/account').then((m) => m.Account),
  },
  {
    path: 'computo-metrico',
    canActivate: [requireAccess(AUTH_POLICIES.access)],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./features/home/home').then((m) => m.Home),
      },
      {
        path: 'create',
        canActivate: [requireAccess(AUTH_POLICIES.manage)],
        loadComponent: () => import('./features/create/create').then((m) => m.Create),
        children: [
          {
            path: 'upload',
            loadComponent: () =>
              import('./features/create/steps/upload-file-step/upload-file-step').then(
                (m) => m.UploadFileStep,
              ),
          },
          {
            path: ':billOfQuantitiesId/review',
            data: { step: 1 },
            canActivate: [billOfQuantitiesStepGuard],
            loadComponent: () =>
              import('./features/create/steps/review-step/review-step').then((m) => m.ReviewStep),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: ':billOfQuantitiesId/suggestions',
            data: { step: 2 },
            canActivate: [billOfQuantitiesStepGuard],
            loadComponent: () =>
              import('./features/create/steps/suggested-work-step/suggested-work-step').then(
                (m) => m.SuggestedWorkStep,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: ':billOfQuantitiesId/summary',
            data: { step: 3 },
            canActivate: [billOfQuantitiesStepGuard],
            loadComponent: () =>
              import('./features/create/steps/summary-step/summary-step').then(
                (m) => m.SummaryStep,
              ),
          },
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'upload',
          },
        ],
      },
      {
        path: ':billOfQuantitiesId/work-item/:workItemId/new-price',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./features/new-price/new-price').then((m) => m.NewPrice),
      },
      {
        path: 'list',
        loadComponent: () => import('./features/list/list').then((m) => m.List),
      },
    ],
  },
  {
    path: 'amministrazione/utenti',
    canActivate: [requireAccess(AUTH_POLICIES.admin)],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/user-management/administration-dashboard').then((m) => m.AdministrationDashboard),
      },
      {
        path: 'nuovo',
        data: { mode: 'create' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-users/user-detail/user-detail').then((m) => m.UserDetail),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'ruoli/nuovo',
        data: { entity: 'role', mode: 'create' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-roles/roles-detail/role-detail').then(
            (m) => m.RoleDetail,
          ),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'ruoli/:name/modifica',
        data: { entity: 'role', mode: 'edit' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-roles/roles-detail/role-detail').then(
            (m) => m.RoleDetail,
          ),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'ruoli/:name',
        data: { entity: 'role', mode: 'detail' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-roles/roles-detail/role-detail').then(
            (m) => m.RoleDetail,
          ),
      },
      {
        path: 'permessi/nuovo',
        data: { entity: 'permission', mode: 'create' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-permissions/permission-detail/permission-detail').then(
            (m) => m.PermissionDetail,
          ),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'permessi/:name/modifica',
        data: { entity: 'permission', mode: 'edit' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-permissions/permission-detail/permission-detail').then(
            (m) => m.PermissionDetail,
          ),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'permessi/:name',
        data: { entity: 'permission', mode: 'detail' },
         loadComponent: () =>
          import('./features/user-management/tabs/tab-permissions/permission-detail/permission-detail').then(
            (m) => m.PermissionDetail,
          ),
      },
      {
        path: ':username/modifica',
        data: { mode: 'edit' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-users/user-detail/user-detail').then((m) => m.UserDetail),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: ':username',
        data: { mode: 'detail' },
        loadComponent: () =>
          import('./features/user-management/tabs/tab-users/user-detail/user-detail').then((m) => m.UserDetail),
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => homeForSession(inject(AuthSession)),
  },
  {
    path: '**',
    redirectTo: () => homeForSession(inject(AuthSession)),
  },
];
